import { z } from 'zod';
import { BUNDLE_PRODUCT_COUNT_MAX, EMPLOYEE_COUNT_MAX, QUANTITY_MAX } from '../limits.js';
import { PRODUCT_CATALOG_COPY } from '../pricing/catalog-copy.js';
import { DiscountPctSchema, EmailSchema, HistoryEntrySchema, IDSchema, IsoTimestampSchema, MoneyCentsSchema, QuoteIdSchema, SchemaVersionSchema, } from './shared.js';
import { PaymentTermsSourceSchema } from './deal.js';
/** Same cap as the intake form's PO number field (`schemas/intake.ts`). */
export const CUSTOMER_PO_NUMBER_MAX = 80;
export const QuoteStateSchema = z.enum(['draft', 'submitted', 'accepted', 'discarded']);
/**
 * T-STRIPE-CHECKOUT — Stripe Invoice.status values we persist on Quote.
 * `open` is the payable state (hosted invoice URL is live). `paid` is
 * terminal-success. `void` / `uncollectible` trigger a new invoice on the
 * next PDF generation.
 */
export const StripeInvoiceStatusSchema = z.enum([
    'draft',
    'open',
    'paid',
    'void',
    'uncollectible',
]);
export const STRIPE_INVOICE_STATUS_DISPLAY = {
    draft: 'Draft',
    open: 'Awaiting payment',
    paid: 'Paid',
    void: 'Voided',
    uncollectible: 'Uncollectible',
};
/**
 * Canonical product-key enum — the single source of truth for the four
 * Right Role products. Persisted/route-boundary values stay the bare
 * lowercase codes (`eh`/`ih`/`sp`/`ta`); these never change. Both the
 * Quote line-item `productKey` (below) and the approval `ProductScope`
 * (schemas/approval.ts) derive from this enum.
 */
export const ProductKeySchema = z.enum(['eh', 'ih', 'sp', 'ta']);
/**
 * Canonical product display labels. Single source of truth — replaces the
 * previously-duplicated `PRODUCT_DISPLAY_NAMES` / `PRODUCT_LABEL` maps that
 * had drifted across QuoteBuilder, the pilot/trial approval detail views and
 * the backend trial-auto-quote integration. Display-only: the underlying
 * enum codes are unchanged. Follows the same convention as
 * `QUOTE_STATE_DISPLAY` and `PAYMENT_TERM_DISPLAY` below.
 *
 * Customer-facing wording: the Blueprint names from the quote PDF fix run
 * (2026-10-27, A1), read from `PRODUCT_CATALOG_COPY` so this map and customer
 * documents can't drift. The retired names (External Hiring, Internal Hiring,
 * Succession Planning, Team Assessments) are gone. This map is the fallback
 * when a pricing config has no `displayName`; the pricing config's
 * `displayName` still takes precedence.
 */
export const PRODUCT_LABELS = {
    eh: PRODUCT_CATALOG_COPY.eh.displayName,
    ih: PRODUCT_CATALOG_COPY.ih.displayName,
    sp: PRODUCT_CATALOG_COPY.sp.displayName,
    ta: PRODUCT_CATALOG_COPY.ta.displayName,
};
/**
 * Resolve a product code to its human-facing label. Accepts the bare enum
 * code; unknown values fall back to the raw input so a future code never
 * renders as `undefined` (it would show the code rather than nothing).
 */
export function productLabel(key) {
    return PRODUCT_LABELS[key] ?? key;
}
/**
 * T10A-006 — friendly display labels for the Quote state machine. The enum
 * values stay the canonical lowercase identifiers (persisted, route-boundary,
 * DB CHECK); this map only governs the user-facing string shown in CRM UIs
 * (e.g. the Quotes tab status badge). Additive — extends the existing
 * display-mapping convention established by `PAYMENT_TERM_DISPLAY` below.
 */
export const QUOTE_STATE_DISPLAY = {
    draft: 'Draft',
    submitted: 'Submitted',
    accepted: 'Accepted',
    discarded: 'Discarded',
};
/**
 * T3-014D — V1 allowed payment terms.
 *
 * Single source of truth for both backend Zod validation and frontend
 * UI selector. The proposal dictates the term (Kenny 2026-06-11), capped at
 * a maximum of NET_60 — the tuple intentionally contains NO term beyond
 * NET_60, so the ceiling is structural. Extend this tuple + the display
 * mapping at packages/backend/src/pdf/quote-pdf-template.ts:PAYMENT_TERM_LABELS
 * (and the DB CHECK migration) to add a new term.
 *
 * The database enforces the same constraint via CHECK on
 * `quotes.payment_terms` (migration 0012).
 */
export const ALLOWED_PAYMENT_TERMS = [
    'UPON_SIGNING',
    'NET_15',
    'NET_30',
    'NET_45',
    'NET_60',
];
/**
 * Default payment term when a quote/pricing-config doesn't specify one.
 * NET_30 preserves the prior single-value behavior.
 */
export const DEFAULT_PAYMENT_TERM = 'NET_30';
export const PaymentTermsSchema = z.enum(ALLOWED_PAYMENT_TERMS);
/**
 * Quote PDF fix run (2026-10-27, A6) — the ONE mapping from a payment-terms
 * value to what a customer reads: a short label and a one-line explanation.
 * The quote PDF, the portal, the Stripe invoice memo and the CRM all read it.
 * A value outside this map is a bug, never something to print: use
 * `paymentTermsCopy()`, which throws.
 *
 * Kenny K2 (2026-10-30): `UPON_SIGNING` reads "Due on acceptance". Acceptance
 * is online now, there is nothing to sign. Display only: the stored value
 * (and the DB CHECK, the route enums, the Stripe due-date rule) is unchanged.
 */
export const PAYMENT_TERMS_COPY = {
    UPON_SIGNING: {
        label: 'Due on acceptance',
        sentence: 'Payment is due in full when this quote is accepted.',
    },
    NET_15: { label: 'Net 15', sentence: 'Payment is due within 15 days of the invoice date.' },
    NET_30: { label: 'Net 30', sentence: 'Payment is due within 30 days of the invoice date.' },
    NET_45: { label: 'Net 45', sentence: 'Payment is due within 45 days of the invoice date.' },
    NET_60: { label: 'Net 60', sentence: 'Payment is due within 60 days of the invoice date.' },
};
/** A6 — how a customer can pay (LP-015 Section 2.2: the Order Form specifies it). */
export const PAYMENT_METHODS_SENTENCE = 'Pay by card or bank transfer (ACH) through the payment link, or by check.';
export class UnknownPaymentTermsError extends Error {
    value;
    constructor(value) {
        super(`Unknown payment terms value: ${JSON.stringify(value)}. Add it to PAYMENT_TERMS_COPY before it can be shown to a customer.`);
        this.value = value;
        this.name = 'UnknownPaymentTermsError';
    }
}
/** Label + sentence for a payment-terms value. Throws on anything unmapped. */
export function paymentTermsCopy(value) {
    if (typeof value === 'string' && Object.prototype.hasOwnProperty.call(PAYMENT_TERMS_COPY, value)) {
        return PAYMENT_TERMS_COPY[value];
    }
    throw new UnknownPaymentTermsError(value);
}
/**
 * Display-string mapping used by the CRM UI selector and summaries. Derived
 * from `PAYMENT_TERMS_COPY` so the CRM and customer documents never disagree.
 */
export const PAYMENT_TERM_DISPLAY = Object.fromEntries(ALLOWED_PAYMENT_TERMS.map((term) => [term, PAYMENT_TERMS_COPY[term].label]));
/** Teams on one Team Assessments line (matches the quote routes). */
const QUOTE_TEAMS_MAX = 1000;
const StandardProductSchema = z.object({
    // Subset of ProductKeySchema — Team Assessments ('ta') has its own schema.
    productKey: z.enum(['eh', 'ih', 'sp']),
    productName: z.string(),
    // QA 2026-10-01 F-08 — the same bounds as the quote routes' line schema.
    quantity: z.number().int().nonnegative().max(QUANTITY_MAX),
    unitPrice: MoneyCentsSchema,
    employeeCount: z.number().int().nonnegative().max(EMPLOYEE_COUNT_MAX).optional(),
    employeeAdderPerUnit: MoneyCentsSchema.optional(),
    lineSubtotal: MoneyCentsSchema,
    volumeDiscountPct: DiscountPctSchema,
    volumeDiscountAmount: MoneyCentsSchema,
    lineNetAfterVolumeDiscount: MoneyCentsSchema,
});
const TeamAssessmentProductSchema = z.object({
    productKey: z.literal('ta'),
    productName: z.string(),
    teams: z.array(z.object({
        teamName: z.string().optional(),
        individualCount: z.number().int().nonnegative().max(EMPLOYEE_COUNT_MAX),
    })),
    teamCount: z.number().int().nonnegative().max(QUOTE_TEAMS_MAX),
    totalIndividuals: z.number().int().nonnegative().max(EMPLOYEE_COUNT_MAX),
    baseFeePerTeam: MoneyCentsSchema,
    feePerIndividual: MoneyCentsSchema,
    lineSubtotal: MoneyCentsSchema,
    volumeDiscountPct: DiscountPctSchema,
    volumeDiscountAmount: MoneyCentsSchema,
    lineNetAfterVolumeDiscount: MoneyCentsSchema,
});
export const QuoteProductSchema = z.union([StandardProductSchema, TeamAssessmentProductSchema]);
export const QuoteSchema = z.object({
    id: QuoteIdSchema,
    type: z.literal('quote'),
    schemaVersion: SchemaVersionSchema,
    accountId: IDSchema,
    state: QuoteStateSchema,
    stateChangedAt: IsoTimestampSchema,
    stateChangedBy: z.string(),
    repEmail: EmailSchema,
    pricingConfigVersion: z.string(),
    products: z.array(QuoteProductSchema),
    bundleProductCount: z.number().int().nonnegative().max(BUNDLE_PRODUCT_COUNT_MAX).default(1),
    bundleDiscountPct: DiscountPctSchema.default(0),
    bundleDiscountAmount: MoneyCentsSchema.default(0),
    customDiscountPct: DiscountPctSchema.optional(),
    customDiscountAmount: MoneyCentsSchema.optional(),
    customDiscountApprovalId: IDSchema.optional(),
    customDiscountJustification: z.string().optional(),
    setupFee: MoneyCentsSchema.default(0),
    setupFeeWaived: z.boolean().default(false),
    // RIG-604 — approval covering a reduced or waived setup fee.
    setupFeeApprovalId: IDSchema.optional(),
    isPilot: z.boolean().default(false),
    pilotApprovalId: IDSchema.optional(),
    conversionCreditAmount: MoneyCentsSchema.optional(),
    isTrial: z.boolean().default(false),
    trialApprovalId: IDSchema.optional(),
    productSubtotal: MoneyCentsSchema,
    totalVolumeDiscount: MoneyCentsSchema.default(0),
    totalBundleDiscount: MoneyCentsSchema.default(0),
    totalCustomDiscount: MoneyCentsSchema.default(0),
    netAfterDiscounts: MoneyCentsSchema,
    setupFeeApplied: MoneyCentsSchema.default(0),
    grandTotal: MoneyCentsSchema,
    // T3-014D — constrained to ALLOWED_PAYMENT_TERMS enum (V1: NET_30 only).
    // Database enforces the same constraint via CHECK on quotes.payment_terms.
    paymentTerms: PaymentTermsSchema,
    paymentTermsSource: PaymentTermsSourceSchema,
    paymentTermsApprovalId: IDSchema.optional(),
    // Quote PDF fix run (2026-10-27, A6) — the customer's purchase-order number,
    // printed in the quote's billing block. Optional; blank prints a write-in
    // line.
    customerPoNumber: z.string().max(CUSTOMER_PO_NUMBER_MAX).optional(),
    // Quote PDF fix run (2026-10-27, B4) — the account's billing state (USPS
    // code), captured by the server on every save. Not client-writable.
    billingState: z.string().max(2).optional(),
    // RIG-500 — set when this quote is a copy carried forward from a superseded
    // proposal. Points at the frozen original.
    supersedesQuoteId: QuoteIdSchema.optional(),
    pdfDocumentId: IDSchema.optional(),
    pdfGeneratedAt: IsoTimestampSchema.optional(),
    // T-STRIPE-CHECKOUT — Stripe hosted-invoice checkout on the Proposal PDF.
    // Created at PDF generation (reused while still open/paid). QBO remains the
    // accounting system of record; Stripe is the customer-facing pay surface.
    stripeInvoiceId: z.string().optional(),
    stripeHostedInvoiceUrl: z.string().url().optional(),
    stripeInvoiceStatus: StripeInvoiceStatusSchema.optional(),
    // T3-008B — 30-day fixed expiration computed at quote create time;
    // not user-editable in V1.
    expiresAt: IsoTimestampSchema,
    submissionId: IDSchema.optional(),
    /**
     * 2026-10-29 staging walk (item 1): the quote's position on its proposal
     * (0 = Option A); null when it is on none. Order with
     * `compareProposalOptions` (pipeline/proposal-options.ts).
     */
    submissionPosition: z.number().int().nonnegative().nullable().optional(),
    history: z.array(HistoryEntrySchema).default([]),
    createdAt: IsoTimestampSchema,
    createdBy: z.string(),
    updatedAt: IsoTimestampSchema,
    updatedBy: z.string(),
    deletedAt: IsoTimestampSchema.nullable().optional(),
    /** QA 2026-10-01 F-42: deleted together with its account (restore brings it back). */
    deletedByCascade: z.boolean().default(false),
    version: z.number().int().positive(),
});
//# sourceMappingURL=quote.js.map